import { useState } from "react";
import { motion } from "framer-motion";
import { X, Sparkles, RotateCcw } from "lucide-react";
import ConfettiBurst from "@/components/ConfettiBurst";
import { useGame } from "@/context/GameContext";
import { sound } from "@/lib/sound";

const StoryMoment = ({ world, onClose }) => {
  const [result, setResult] = useState(null);
  const [burstKey, setBurstKey] = useState(0);
  const { setStory } = useGame();
  const { prompt, choices } = world.storyMoment;

  const choose = (c) => {
    setResult(c);
    setStory(world.id, c.id);
    sound.playBurst(world.id);
    setBurstKey((k) => k + 1);
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-5"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="story-moment-overlay"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        className="relative w-full max-w-lg overflow-hidden rounded-3xl border bg-black/80 p-8 backdrop-blur-2xl"
        style={{ borderColor: `${world.accent}66`, boxShadow: `0 0 60px ${world.accentSoft}` }}
        initial={{ scale: 0.92, y: 20, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.92, y: 20, opacity: 0 }}
        transition={{ type: "spring", stiffness: 240, damping: 24 }}
      >
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-1"
          style={{ background: world.accent }}
        />
        <button
          onClick={onClose}
          data-testid="story-close"
          className="absolute right-4 top-4 rounded-full p-2 text-white/50 transition-colors hover:bg-white/10 hover:text-white"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="mb-5 flex items-center gap-2" style={{ color: world.accent }}>
          <Sparkles className="h-5 w-5" />
          <span className="text-xs uppercase tracking-[0.25em]">Story Moment</span>
        </div>

        <p className="text-lg leading-relaxed text-white/90" data-testid="story-prompt">
          {prompt}
        </p>

        {!result ? (
          <div className="mt-7 space-y-3">
            {choices.map((c) => (
              <button
                key={c.id}
                onClick={() => choose(c)}
                data-testid={`story-choice-${c.id}`}
                className="group flex w-full items-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-4 text-left transition-colors hover:bg-white/10"
                style={{ borderColor: "rgba(255,255,255,0.1)" }}
              >
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-display text-sm text-black"
                  style={{ background: world.accent }}
                >
                  {c.id}
                </span>
                <span className="text-sm text-white/85">{c.label}</span>
              </button>
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-7"
            data-testid="story-result"
          >
            <p
              className="rounded-2xl border p-5 text-sm leading-relaxed text-white/85"
              style={{ borderColor: `${world.accent}55`, background: `${world.accent}14` }}
            >
              {result.result}
            </p>
            <div className="mt-5 flex gap-3">
              <button
                onClick={() => setResult(null)}
                data-testid="story-replay"
                className="flex flex-1 items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 py-2.5 text-sm text-white/80 transition-colors hover:text-white"
              >
                <RotateCcw className="h-4 w-4" /> Choose again
              </button>
              <button
                onClick={onClose}
                className="flex-1 rounded-full py-2.5 text-sm font-medium text-black transition-transform hover:scale-[1.02]"
                style={{ background: world.accent }}
              >
                Continue
              </button>
            </div>
          </motion.div>
        )}
      </motion.div>
      {result && <ConfettiBurst key={burstKey} color={world.accent} />}
    </motion.div>
  );
};

export default StoryMoment;
